// Tiller Mapping View — side-by-side of app account display_name, Tiller
// account reference, Tiller ID, last-upload-seen, status. Sortable,
// filterable, exportable to CSV. Admin/senior can inline-edit a row's
// Tiller link (calls /accounts/:id/link-tiller or /unlink-tiller, which
// also append tiller_mapping_history).

import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { accounts as accountsApi, ApiError, mapping } from '@/lib/api';
import { useMe } from '@/hooks/useMe';
import type { MappingListQuery, MappingRow, TillerStatus } from '@frt/shared-types';
import { csvCell } from '@frt/shared-types';

type SortKey =
  | 'display_name'
  | 'frt_customer_code'
  | 'tiller_account_reference'
  | 'tiller_account_id'
  | 'last_seen_uploaded_at'
  | 'tiller_status';

const PER_PAGE = 50;

export function TillerMappingPage() {
  const { data: me } = useMe();
  const canEdit = me?.role === 'admin' || me?.role === 'senior';
  const [q, setQ] = useState('');
  const [tillerStatus, setTillerStatus] = useState<TillerStatus | ''>('');
  const [hasTiller, setHasTiller] = useState<'' | 'linked' | 'unlinked'>('');
  const [sort, setSort] = useState<SortKey>('display_name');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [page, setPage] = useState(1);

  const query: MappingListQuery = {
    q: q || undefined,
    tiller_status: (tillerStatus || undefined) as TillerStatus | undefined,
    has_tiller: (hasTiller || undefined) as 'linked' | 'unlinked' | undefined,
    sort,
    dir,
    page,
    per_page: PER_PAGE,
  };

  const onSort = (key: SortKey) => {
    if (key === sort) {
      setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      // Dates default to "newest first"; everything else asc.
      setDir(key === 'last_seen_uploaded_at' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const { data, isLoading, error } = useQuery({
    queryKey: ['mapping', query],
    queryFn: () => mapping.list(query),
    staleTime: 15_000,
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const firstLineNumber = (page - 1) * PER_PAGE + 1;

  const exportCsv = () => {
    const header = [
      '#',
      'frt_customer_code',
      'frt_customer_name',
      'display_name',
      'tiller_account_reference',
      'tiller_account_id',
      'tiller_status',
      'account_status',
      'last_seen_uploaded_at',
    ].join(',');
    const lines = rows.map((r, i) =>
      [
        String(firstLineNumber + i),
        csvCell(r.frt_customer_code),
        csvCell(r.frt_customer_name),
        csvCell(r.display_name),
        csvCell(r.tiller_account_reference ?? ''),
        csvCell(r.tiller_account_id ?? ''),
        csvCell(r.tiller_status),
        csvCell(r.account_status),
        csvCell(r.last_seen_uploaded_at ?? ''),
      ].join(','),
    );
    const csv = `${header}\n${lines.join('\n')}\n`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tiller-mapping-page${page}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-slate-900">Tiller Mapping</h1>
        <Button variant="secondary" onClick={exportCsv} disabled={rows.length === 0}>
          Export CSV
        </Button>
      </div>

      <Card>
        <CardBody className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Input
              placeholder="Search display name / Tiller ref / ID"
              value={q}
              onChange={(e) => {
                setPage(1);
                setQ(e.target.value);
              }}
            />
            <select
              className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
              value={tillerStatus}
              onChange={(e) => {
                setPage(1);
                setTillerStatus(e.target.value as TillerStatus | '');
              }}
            >
              <option value="">All Tiller statuses</option>
              <option value="integrated">Integrated</option>
              <option value="login_issues">Login issues</option>
              <option value="manual">Manual</option>
              <option value="new">New</option>
            </select>
            <select
              className="rounded border border-slate-300 bg-white px-2 py-2 text-sm"
              value={hasTiller}
              onChange={(e) => {
                setPage(1);
                setHasTiller(e.target.value as '' | 'linked' | 'unlinked');
              }}
            >
              <option value="">Linked + unlinked</option>
              <option value="linked">Linked only</option>
              <option value="unlinked">Unlinked only</option>
            </select>
          </div>
          <div className="text-xs text-slate-500">
            Click any column header to sort.
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{total} accounts</CardTitle>
        </CardHeader>
        <CardBody>
          {error && <div className="text-sm text-red-600">{(error as Error).message}</div>}
          {isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-slate-500">
                  <tr>
                    <th className="py-1 pr-3 w-12 text-right font-mono">#</th>
                    <SortableTh
                      label="Customer"
                      field="frt_customer_code"
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Display name"
                      field="display_name"
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Tiller account reference"
                      field="tiller_account_reference"
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Tiller account ID"
                      field="tiller_account_id"
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Status"
                      field="tiller_status"
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                    />
                    <SortableTh
                      label="Last seen"
                      field="last_seen_uploaded_at"
                      sort={sort}
                      dir={dir}
                      onSort={onSort}
                    />
                    {canEdit && <th className="py-1 pr-3 text-right">Edit</th>}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r: MappingRow, i) => (
                    <MappingTableRow
                      key={r.account_id}
                      row={r}
                      lineNumber={firstLineNumber + i}
                      canEdit={canEdit}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="mt-3 flex items-center justify-between text-sm">
            <div className="text-slate-500">
              Page {page} of {totalPages}
            </div>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}


// Click-to-sort header. Active column shows ▲/▼; inactive columns show a
// faded ↕ glyph so it reads as interactive.
function SortableTh({
  label,
  field,
  sort,
  dir,
  onSort,
}: {
  label: string;
  field: SortKey;
  sort: SortKey;
  dir: 'asc' | 'desc';
  onSort: (k: SortKey) => void;
}) {
  const active = sort === field;
  const arrow = active ? (dir === 'asc' ? '▲' : '▼') : '↕';
  return (
    <th className="py-1 pr-3">
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`cursor-pointer select-none hover:text-slate-900 ${
          active ? 'text-slate-900' : ''
        }`}
      >
        {label}
        <span className={`ml-1 ${active ? 'text-slate-700' : 'text-slate-300'}`}>
          {arrow}
        </span>
      </button>
    </th>
  );
}

// One row in the mapping table. Read-only by default; expands into editable
// inputs when the operator clicks Edit. Save calls /accounts/:id/link-tiller
// (which appends a tiller_mapping_history entry) when the Tiller ID is
// present, or /unlink-tiller when both fields are cleared. Both endpoints
// gate on requireRole('admin', 'senior') server-side.
function MappingTableRow({
  row,
  lineNumber,
  canEdit,
}: {
  row: MappingRow;
  lineNumber: number;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [tillerId, setTillerId] = useState(row.tiller_account_id ?? '');
  const [tillerRef, setTillerRef] = useState(row.tiller_account_reference ?? '');
  const [error, setError] = useState<string | null>(null);

  // If the underlying row changes (page change, refetch) while we're not in
  // edit mode, sync the local state. Don't clobber in-progress edits.
  useEffect(() => {
    if (!editing) {
      setTillerId(row.tiller_account_id ?? '');
      setTillerRef(row.tiller_account_reference ?? '');
    }
  }, [row.tiller_account_id, row.tiller_account_reference, editing]);

  const save = useMutation({
    mutationFn: async () => {
      const trimmedId = tillerId.trim();
      const trimmedRef = tillerRef.trim();
      if (trimmedId) {
        return accountsApi.linkTiller(row.account_id, {
          tiller_account_id: trimmedId,
          tiller_account_reference: trimmedRef || null,
        });
      }
      // Empty Tiller ID → unlink (only meaningful if currently linked).
      if (row.tiller_account_id) {
        return accountsApi.unlinkTiller(row.account_id, {});
      }
      // No-op: nothing to save.
      return null;
    },
    onSuccess: () => {
      setError(null);
      setEditing(false);
      void queryClient.invalidateQueries({ queryKey: ['mapping'] });
      void queryClient.invalidateQueries({ queryKey: ['accounts'] });
      void queryClient.invalidateQueries({ queryKey: ['account', row.account_id] });
    },
    onError: (e: unknown) => {
      const msg = e instanceof ApiError ? e.message : (e as Error).message;
      setError(msg);
    },
  });

  if (!editing) {
    return (
      <tr className="border-t border-slate-100">
        <td className="py-1 pr-3 text-right text-slate-500 font-mono">{lineNumber}</td>
        <td className="py-1 pr-3 text-slate-700" title={row.frt_customer_name}>
          {row.frt_customer_code}
        </td>
        <td className="py-1 pr-3 font-medium text-slate-900">{row.display_name}</td>
        <td className="py-1 pr-3 text-slate-700">
          {row.tiller_account_reference ?? <span className="text-slate-400">—</span>}
        </td>
        <td className="py-1 pr-3 font-mono text-xs text-slate-700">
          {row.tiller_account_id ?? <span className="text-slate-400">—</span>}
        </td>
        <td className="py-1 pr-3">
          <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
            {row.tiller_status}
          </span>
        </td>
        <td className="py-1 pr-3 text-xs text-slate-600">
          {row.last_seen_uploaded_at?.slice(0, 10) ?? '—'}
        </td>
        {canEdit && (
          <td className="py-1 pr-3 text-right">
            <Button size="sm" variant="ghost" onClick={() => setEditing(true)}>
              Edit
            </Button>
          </td>
        )}
      </tr>
    );
  }

  const dirty =
    tillerId.trim() !== (row.tiller_account_id ?? '') ||
    tillerRef.trim() !== (row.tiller_account_reference ?? '');

  return (
    <tr className="border-t border-slate-100 bg-slate-50 align-top">
      <td className="py-1 pr-3 text-right text-slate-500 font-mono">{lineNumber}</td>
      <td className="py-1 pr-3 text-slate-700" title={row.frt_customer_name}>
        {row.frt_customer_code}
      </td>
      <td className="py-1 pr-3 font-medium text-slate-900">{row.display_name}</td>
      <td className="py-1 pr-3">
        <Input
          value={tillerRef}
          onChange={(e) => setTillerRef(e.target.value)}
          placeholder="Bank — Owner — Type ...1234"
        />
      </td>
      <td className="py-1 pr-3">
        <Input
          value={tillerId}
          onChange={(e) => setTillerId(e.target.value)}
          placeholder="Tiller GUID (Account ID column)"
          className="font-mono text-xs"
        />
        {error && <div className="mt-1 text-xs text-red-600">{error}</div>}
      </td>
      <td className="py-1 pr-3">
        <span className="inline-block rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-700">
          {row.tiller_status}
        </span>
      </td>
      <td className="py-1 pr-3 text-xs text-slate-600">
        {row.last_seen_uploaded_at?.slice(0, 10) ?? '—'}
      </td>
      <td className="py-1 pr-3 text-right whitespace-nowrap">
        <div className="inline-flex gap-1">
          <Button
            size="sm"
            onClick={() => save.mutate()}
            disabled={save.isPending || !dirty}
          >
            {save.isPending ? 'Saving…' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setTillerId(row.tiller_account_id ?? '');
              setTillerRef(row.tiller_account_reference ?? '');
              setError(null);
              setEditing(false);
            }}
            disabled={save.isPending}
          >
            Cancel
          </Button>
        </div>
      </td>
    </tr>
  );
}
